import { MedplumClient } from "@medplum/core";
import { Encounter } from "@medplum/fhirtypes";

import {
  EncounterTimeSpentInput,
  UpdateEncounterTimeSpentResult,
} from "@/encounters/types/encounter-time-spent";
import { applyEncounterTimeSpent } from "@/encounters/utils/encounter-time-spent";
import { ifMatchVersionOptions } from "@/utils/medplum-if-match";
import { isMedplumIfMatchConflictError } from "@/utils/medplum-if-match-conflict";

/**
 * Saves the time spent on an encounter onto the latest version of the Encounter, so a stale copy
 * held by the page can't overwrite other fields written since it was loaded. The read bypasses the
 * client cache, and the write carries `If-Match` for the version read, so a change landing between
 * the two is refused instead of overwritten.
 *
 * @param medplum Medplum client used to read and update the Encounter.
 * @param encounterId Encounter id to update.
 * @param values Face-to-face and care coordination minutes to persist.
 * @returns The updated Encounter; `version_conflict` when the Encounter changed between the read
 *   and the write; `update_failed` when the read or the write fails for any other reason.
 */
export async function updateEncounterTimeSpent(
  medplum: MedplumClient,
  encounterId: string,
  values: EncounterTimeSpentInput,
): Promise<UpdateEncounterTimeSpentResult> {
  try {
    const latestEncounter = await medplum.readResource("Encounter", encounterId, {
      cache: "no-cache",
    });
    const savedEncounter = await medplum.updateResource<Encounter>(
      applyEncounterTimeSpent(latestEncounter, values),
      ifMatchVersionOptions(latestEncounter),
    );
    return { ok: true, data: savedEncounter };
  } catch (error) {
    if (isMedplumIfMatchConflictError(error)) return { ok: false, reason: "version_conflict" };

    return { ok: false, reason: "update_failed" };
  }
}
